import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';
import { handleCheckout, getPaymentLink } from '../lib/stripe';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const Pricing = () => {
  const { language } = useLanguage();
  const text = t[language].pricing;
  const [loading, setLoading] = useState(null);
  const [viewers, setViewers] = useState(47);

  useEffect(() => {
    let currentViewers = Math.floor(Math.random() * 40) + 35;
    setViewers(currentViewers);
    const interval = setInterval(() => {
      const change = Math.floor(Math.random() * 5) - 2;
      currentViewers = Math.min(85, Math.max(25, currentViewers + change));
      setViewers(currentViewers);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const onPurchase = async (planType) => {
    const url = getPaymentLink(language, planType);
    setLoading(planType);
    try { handleCheckout(url); } catch (e) { console.error(e); } finally { setLoading(null); }
  };

  return (
    <section id="pricing" className="py-16 sm:py-24 bg-[#0a0a0f] relative border-t border-white/5 overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808010_1px,transparent_1px),linear-gradient(to_bottom,#80808010_1px,transparent_1px)] bg-[size:24px_24px]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-orange-600/10 rounded-full blur-[120px]" />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs sm:text-sm font-bold mb-5 sm:mb-6">
            <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
            {text.viewing.replace('{count}', viewers)}
          </motion.div>
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            {text.title}
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="text-base sm:text-xl text-gray-400">
            {text.subtitle}
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
          {/* Weekly */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} whileHover={{ y: -4 }} transition={{ duration: 0.5, delay: 0.1 }} className="bg-[#1a1a24] rounded-3xl p-6 sm:p-8 border border-white/10 flex flex-col hover:border-orange-500/30 transition-colors group relative">
            <h3 className="text-lg font-bold text-gray-400 mb-3 group-hover:text-white transition-colors">{text.weekly.name}</h3>
            <div className="flex items-end gap-1 mb-6">
              <span className="text-4xl sm:text-5xl font-black text-white">{text.weekly.price}</span>
              <span className="text-xl font-bold text-gray-500 mb-1">{text.weekly.cents}</span>
            </div>
            <div className="flex-grow space-y-4 mb-8">
              {text.weekly.features.map((feat, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <SafeIcon icon={FiIcons.FiCheck} className="text-orange-400 shrink-0 mt-1 text-sm" />
                  <span className="text-gray-300 text-sm">{feat}</span>
                </div>
              ))}
            </div>
            <button
              disabled={loading !== null}
              onClick={() => onPurchase('weekly')}
              className="w-full py-4 rounded-xl bg-white/5 text-white font-bold border border-white/10 hover:bg-white/10 transition-all flex items-center justify-center gap-2"
            >
              {loading === 'weekly' ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : text.cta}
            </button>
          </motion.div>

          {/* Monthly */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} whileHover={{ y: -8 }} transition={{ duration: 0.5, delay: 0.2 }} className="bg-gradient-to-b from-[#12121e] to-[#1a1a24] rounded-3xl p-6 sm:p-8 border-2 border-orange-500 flex flex-col relative shadow-2xl shadow-orange-500/20">
            {text.monthly.badge && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-orange-500 to-purple-500 text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap shadow-lg">
                {text.monthly.badge}
              </div>
            )}
            <h3 className="text-lg font-bold text-orange-400 mb-3">{text.monthly.name}</h3>
            <div className="flex items-end gap-1 mb-6">
              <span className="text-4xl sm:text-5xl font-black text-white">{text.monthly.price}</span>
              <span className="text-xl font-bold text-gray-400 mb-1">{text.monthly.cents}</span>
            </div>
            <div className="flex-grow space-y-4 mb-8">
              {text.monthly.features.map((feat, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <SafeIcon icon={FiIcons.FiCheck} className="text-orange-400 shrink-0 mt-1 text-sm" />
                  <span className="text-white font-medium text-sm">{feat}</span>
                </div>
              ))}
            </div>
            <button
              disabled={loading !== null}
              onClick={() => onPurchase('monthly')}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-orange-500 to-purple-600 text-white font-black transition-all flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(249,115,22,0.4)]"
            >
              {loading === 'monthly' ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>{text.ctaHighlight}</>
              )}
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;